import {
  MAIL_TEMPLATE_KINDS,
  MAIL_TEMPLATE_LABELS,
  MAIL_VARIABLES,
  type MailSettings,
  type MailSettingsInput,
  type MailTemplate,
  type MailTemplateKind,
  mailSettingsInput,
} from '@facture/contracts'
import { zodResolver } from '@hookform/resolvers/zod'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { MailIcon, PlugZapIcon, ServerIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import { Field } from '@/components/ui/field'
import { Input, Textarea } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'

export const Route = createFileRoute('/_app/settings/email')({
  staticData: { crumb: 'Correo' },
  component: EmailSettingsPage,
})

const settingsQuery = queryOptions({
  queryKey: ['mail-settings'],
  queryFn: () => api.get<MailSettings>('/mail/settings'),
})
const templatesQuery = queryOptions({
  queryKey: ['mail-templates'],
  queryFn: () => api.get<MailTemplate[]>('/mail/templates'),
})

function EmailSettingsPage() {
  return (
    <div className="grid gap-6">
      <SmtpCard />
      <TemplatesCard />
    </div>
  )
}

function SmtpCard() {
  const { data } = useQuery(settingsQuery)
  const queryClient = useQueryClient()
  const form = useForm<MailSettingsInput>({
    resolver: zodResolver(mailSettingsInput),
    values: data
      ? {
          host: data.host ?? '',
          port: data.port,
          secure: data.secure,
          username: data.username,
          password: '',
          fromName: data.fromName,
          fromEmail: data.fromEmail ?? '',
          replyTo: data.replyTo ?? '',
          bcc: data.bcc ?? '',
        }
      : undefined,
  })
  const save = useMutation({
    mutationFn: (values: MailSettingsInput) => api.put<MailSettings>('/mail/settings', values),
    onSuccess: (s) => {
      queryClient.setQueryData(settingsQuery.queryKey, s)
      toast.success('Servidor de correo guardado')
    },
  })
  const verify = useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>('/mail/verify'),
    onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)),
  })
  if (!data) return <Skeleton className="h-80 rounded-xl" />
  const { errors } = form.formState

  return (
    <Card>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardHeader>
          <div>
            <CardTitle className="flex items-center gap-2">
              <ServerIcon className="size-4 text-muted-foreground" /> Servidor de correo (SMTP)
            </CardTitle>
            <CardDescription>
              Enviá los comprobantes desde tu propio dominio. Sin configurar, se usa el servidor de Facture.
            </CardDescription>
          </div>
          <Badge variant={data.configured ? 'success' : 'neutral'}>
            {data.configured ? 'Propio' : 'Del sistema'}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_7rem_auto] sm:items-end">
            <Field label="Servidor" error={errors.host} required>
              {(c) => <Input {...c} {...form.register('host')} placeholder="smtp.gmail.com" />}
            </Field>
            <Field label="Puerto" error={errors.port}>
              {(c) => <Input {...c} {...form.register('port')} type="number" />}
            </Field>
            <Controller
              control={form.control}
              name="secure"
              render={({ field }) => (
                <label className="flex h-9 items-center gap-2 text-sm">
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} /> SSL/TLS
                </label>
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Usuario" error={errors.username}>
              {(c) => <Input {...c} {...form.register('username')} autoComplete="off" />}
            </Field>
            <Field
              label="Contraseña"
              aside={
                data.hasPassword ? (
                  <span className="text-muted-foreground text-xs">Vacía = conservar la guardada</span>
                ) : undefined
              }
            >
              {(c) => (
                <Input
                  {...c}
                  {...form.register('password')}
                  type="password"
                  autoComplete="new-password"
                  placeholder={data.hasPassword ? '••••••••' : ''}
                />
              )}
            </Field>
            <Field label="Nombre del remitente" error={errors.fromName}>
              {(c) => <Input {...c} {...form.register('fromName')} placeholder="Facturación · Mi Empresa" />}
            </Field>
            <Field label="Correo del remitente" error={errors.fromEmail} required>
              {(c) => <Input {...c} {...form.register('fromEmail')} type="email" />}
            </Field>
            <Field label="Responder a" error={errors.replyTo}>
              {(c) => <Input {...c} {...form.register('replyTo')} type="email" placeholder="Opcional" />}
            </Field>
            <Field label="Copia oculta (archivo)" error={errors.bcc}>
              {(c) => <Input {...c} {...form.register('bcc')} type="email" placeholder="Opcional" />}
            </Field>
          </div>
        </CardContent>
        <CardFooter className="justify-between">
          <div className="flex gap-2">
            {data.configured && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  loading={verify.isPending}
                  onClick={() => verify.mutate()}
                >
                  <PlugZapIcon /> Probar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    confirm({
                      title: '¿Volver al servidor del sistema?',
                      description: 'Se borra la configuración SMTP propia.',
                      destructive: true,
                      onConfirm: async () => {
                        await api.delete('/mail/settings')
                        await queryClient.invalidateQueries({ queryKey: ['mail-settings'] })
                      },
                    })
                  }
                >
                  <Trash2Icon /> Quitar
                </Button>
              </>
            )}
          </div>
          <Button type="submit" loading={save.isPending}>
            Guardar
          </Button>
        </CardFooter>
      </form>
    </Card>
  )
}

const DOCUMENT_SAMPLE: Record<MailTemplateKind, string> = {
  document: 'factura electrónica',
  ticket: 'tiquete electrónico',
  credit_note: 'nota de crédito electrónica',
  debit_note: 'nota de débito electrónica',
  purchase: 'factura electrónica de compra',
  quote: 'cotización',
  reminder: 'factura electrónica',
}

function TemplatesCard() {
  const { data } = useQuery(templatesQuery)
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<MailTemplateKind>('document')
  const current = data?.find((t) => t.kind === kind)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  useEffect(() => {
    if (current) {
      setSubject(current.subject)
      setBody(current.body)
    }
  }, [current])
  const save = useMutation({
    mutationFn: () => api.put<MailTemplate[]>(`/mail/templates/${kind}`, { subject, body }),
    onSuccess: (t) => {
      queryClient.setQueryData(templatesQuery.queryKey, t)
      toast.success('Plantilla guardada')
    },
  })
  const sample: Record<string, string> = {
    '{cliente}': 'Ana María Soto',
    '{compania}': 'Soluciones Demo',
    '{documento}': DOCUMENT_SAMPLE[kind],
    '{numero}': '1024',
    '{total}': '₡103 215,00',
    '{vence}': '30/10/2026',
    '{clave}': '5063009260031010000…',
  }
  const preview = (text: string) => text.replace(/\{\w+\}/g, (m) => sample[m] ?? m)

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <MailIcon className="size-4 text-muted-foreground" /> Plantillas
          </CardTitle>
          <CardDescription>
            Una plantilla por tipo de comprobante, más cotización y recordatorio de pago.
          </CardDescription>
        </div>
        <Select
          value={kind}
          onValueChange={(v) => setKind(v as MailTemplateKind)}
          options={MAIL_TEMPLATE_KINDS.map((k) => ({ value: k, label: MAIL_TEMPLATE_LABELS[k] }))}
          className="w-52"
        />
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="grid content-start gap-4">
          <Field label="Asunto">
            {(c) => <Input {...c} value={subject} onChange={(e) => setSubject(e.target.value)} />}
          </Field>
          <Field label="Mensaje">
            {(c) => <Textarea {...c} value={body} onChange={(e) => setBody(e.target.value)} rows={10} />}
          </Field>
          <div className="flex flex-wrap gap-1.5">
            {MAIL_VARIABLES.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setBody((b) => `${b}${v}`)}
                className="rounded-md bg-muted px-2 py-0.5 font-mono text-muted-foreground text-xs transition hover:bg-accent hover:text-foreground"
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-subtle p-4">
          <div className="mb-3 font-medium text-muted-foreground text-xs">Vista previa</div>
          <div className="rounded-lg border border-border bg-card p-5 shadow-elevation-1">
            <div className="font-semibold text-sm">{preview(subject)}</div>
            <div className="mt-3 whitespace-pre-line text-muted-foreground text-sm leading-relaxed">
              {preview(body)}
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-between">
        <span className="text-muted-foreground text-xs">
          {current?.isDefault ? 'Usando la plantilla predeterminada' : 'Plantilla personalizada'}
        </span>
        <Button
          loading={save.isPending}
          onClick={() => save.mutate()}
          disabled={subject.length < 3 || body.length < 10}
        >
          Guardar plantilla
        </Button>
      </CardFooter>
    </Card>
  )
}
